import { createRouter, createWebHistory } from "vue-router";
import { watch } from "vue";
import { trackPage } from "../utils/analytics";
import { useAuthStore } from "../stores/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore"; // Import Firestore methods
import { db } from "../lib/firebase"; // Import DB instance
import { showAdmin, isNative } from "../utils/platform";
import Home from "../pages/public/Home.vue";

// LAZY LOADED ROUTES : Admin (conditionally loaded based on build target)
const AdminCommandes = () => import("../pages/admin/AdminCommandes.vue");
const AdminCreneaux = () => import("../pages/admin/AdminCreneaux.vue");
const AdminDashboard = () => import("../pages/admin/AdminDashboard.vue");
const AdminStock = () => import("../pages/admin/AdminStock.vue");
const AdminPlats = () => import("../pages/admin/AdminPlats.vue");
const AdminLivraison = () => import("../pages/admin/AdminLivraison.vue");
const AdminClients = () => import("../pages/admin/AdminClients.vue");
const AdminCommunication = () => import("../pages/admin/AdminCommunication.vue");
const AdminAnalytics = () => import("../pages/admin/AdminAnalytics.vue");
const AdminPlanning = () => import("../pages/admin/AdminPlanning.vue");
const AdminAteliers = () => import("../pages/admin/AdminAteliers.vue");
const AdminAtelierEdit = () => import("../pages/admin/AdminAtelierEdit.vue");
const AdminAtelierInscriptions = () => import("../pages/admin/AdminAtelierInscriptions.vue");
const AdminBlocs = () => import('../pages/admin/AdminBlocs.vue');
const AdminCatalogue = () => import('../pages/admin/AdminCatalogue.vue');
const AdminEtiquettes = () => import('../pages/admin/AdminEtiquettes.vue');
const AdminProductionsV2 = () => import('../pages/admin/AdminProductionsV2.vue');
const AdminProductionV2Hub = () => import('../pages/admin/AdminProductionV2Hub.vue');

const AuthConnexion = () => import("../pages/auth/AuthConnexion.vue");
const AuthInscription = () => import("../pages/auth/AuthInscription.vue");
const AuthMotDePasseOublie = () => import("../pages/auth/AuthMotDePasseOublie.vue");

const CommandeEntreprise = () => import("../pages/commande/CommandeEntreprise.vue");
const CommandeMaison = () => import("../pages/commande/CommandeMaison.vue");
const CommandeSuivi = () => import("../pages/commande/CommandeSuivi.vue");

const CompteCommandes = () => import("../pages/compte/CompteCommandes.vue");
const ComptePlats = () => import("../pages/compte/ComptePlats.vue");
const CompteProfil = () => import("../pages/compte/CompteProfil.vue");
const CompteInfos = () => import("../pages/compte/CompteInfos.vue");
const CompteNotifications = () => import("../pages/compte/CompteNotifications.vue");
const ComptePreferences = () => import("../pages/compte/ComptePreferences.vue");
const QuizGouts = () => import('../pages/compte/QuizGouts.vue');
const CompteGouts = () => import('../pages/compte/CompteGouts.vue');
const CompteParrainage = () => import("../pages/compte/CompteParrainage.vue");
const CompteAteliers = () => import("../pages/compte/CompteAteliers.vue");
const CompteParametres = () => import("../pages/compte/CompteParametres.vue");

const Contact = () => import("../pages/public/Contact.vue");
const EntreprisePublic = () => import("../pages/public/EntreprisePublic.vue");
const Faq = () => import("../pages/public/Faq.vue");
const ParrainageInfo = () => import("../pages/public/ParrainageInfo.vue");
const Conservation = () => import("../pages/public/Conservation.vue");
const Cgv = () => import("../pages/public/Cgv.vue");
const Livraison = () => import("../pages/public/Livraison.vue");
const MentionsLegales = () => import("../pages/public/MentionsLegales.vue");
const Concept = () => import("../pages/public/Concept.vue");
const Application = () => import("../pages/public/Application.vue");
const Desabonnement = () => import("../pages/public/Desabonnement.vue");
const PublicLot = () => import("../pages/public/PublicLot.vue");
const JoinCompany = () => import("../pages/public/JoinCompany.vue");
const Ateliers = () => import("../pages/public/Ateliers.vue");
const AtelierDetail = () => import("../pages/public/AtelierDetail.vue");
const NoterPlat = () => import('../pages/public/NoterPlat.vue');
const WebWelcome = () => import("../pages/public/WebWelcome.vue");

const NotFound = () => import("../pages/NotFound.vue");
const ProtoA = () => import("../pages/protos/ProtoA.vue");

// Native-only pages (lazy loaded)
const NativeWelcome = () => import("../pages/native/NativeWelcome.vue");
const NativeHome = () => import("../pages/native/NativeHome.vue");
const NativeEmailAuth = () => import("../pages/native/NativeEmailAuth.vue");
const NativeConcept = () => import("../pages/native/NativeConcept.vue");

const routes = [
  { path: "/", name: "home", component: isNative ? NativeHome : Home },
  // Native auth gate — only used in native app for non-logged users
  ...(isNative ? [
    { path: "/welcome", name: "native-welcome", component: NativeWelcome, meta: { guestOnly: true } },
    { path: "/decouvrir", name: "native-concept", component: NativeConcept },
  ] : []),
  { path: "/rejoindre", name: "join-company", component: JoinCompany },
  // Onboarding cinematique web : joue a la premiere connexion apres inscription
  // (web only ; le natif utilise /welcome).
  ...(!isNative ? [
    { path: "/bienvenue", name: "web-welcome", component: WebWelcome, meta: { requiresAuth: true } },
  ] : []),
  { path: '/note/:token', name: 'noter-plat', component: NoterPlat, meta: { public: true } },
  { path: "/concept", name: "concept", component: Concept },
  { path: "/application", name: "application", component: Application },
  // Cible du lien "Se desabonner" des emails marketing. Publique et sans login.
  { path: "/desabonnement", name: "desabonnement", component: Desabonnement },
  { path: "/maison", redirect: "/concept" },
  { path: "/entreprise", name: "entreprise-public", component: EntreprisePublic },
  { path: "/faq", name: "faq", component: Faq },
  { path: "/parrainage", name: "parrainage-info", component: ParrainageInfo },
  { path: "/contact", name: "contact", component: Contact },
  { path: "/inauguration", name: "inauguration", component: () => import("../pages/public/Inauguration.vue") },
  { path: "/ateliers", name: "ateliers", component: Ateliers },
  { path: "/ateliers/:slug", name: "atelier-detail", component: AtelierDetail, props: true },
  { path: "/ateliers/:slug/confirmation", name: "atelier-confirmation", component: AtelierDetail, props: true },
  { path: "/conservation", name: "conservation", component: Conservation },
  { path: "/livraison", name: "livraison", component: Livraison },
  { path: "/cgv", name: "cgv", component: Cgv },
  { path: "/mentions-legales", name: "mentions-legales", component: MentionsLegales },
  {
    path: "/public/lot/:productionId/:platId",
    name: "public-lot",
    component: PublicLot,
    props: true,
  },
  { path: "/auth/inscription", name: "inscription", component: AuthInscription },
  { path: "/auth/connexion", name: "connexion", component: AuthConnexion },
  ...(isNative ? [{ path: "/auth/email", name: "native-email-auth", component: NativeEmailAuth }] : []),
  {
    path: "/auth/mot-de-passe-oublie",
    name: "mot-de-passe-oublie",
    component: AuthMotDePasseOublie,
  },
  { path: "/compte", name: "compte", component: CompteProfil, meta: { requiresAuth: true } },
  { path: "/compte/infos", name: "compte-infos", component: CompteInfos, meta: { requiresAuth: true } },
  { path: "/compte/notifications", name: "compte-notifications", component: CompteNotifications, meta: { requiresAuth: true } },
  { path: "/compte/commandes", name: "compte-commandes", component: CompteCommandes, meta: { requiresAuth: true } },
  { path: "/compte/plats", name: "compte-plats", component: ComptePlats, meta: { requiresAuth: true } },
  { path: "/compte/entreprise", name: "compte-entreprise", component: () => import("../pages/compte/CompteEntreprise.vue"), meta: { requiresAuth: true } },
  // /compte/profils est géré par le widget sur /compte → on redirige
  { path: '/compte/profils', redirect: '/compte' },
  { path: '/compte/profils/:id/gouts', name: 'compte-gouts', component: QuizGouts, meta: { requiresAuth: true, guestOnboarding: true } },
  { path: '/compte/profils/:id/mes-gouts', name: 'compte-mes-gouts', component: CompteGouts, meta: { requiresAuth: true } },
  // Ancien lien /quiz → redirige vers /gouts pour compat
  { path: '/compte/profils/:id/quiz', redirect: (to) => `/compte/profils/${to.params.id}/gouts` },
  { path: "/compte/preferences", name: "compte-preferences", component: ComptePreferences, meta: { requiresAuth: true } },
  { path: "/compte/parrainage", name: "compte-parrainage", component: CompteParrainage, meta: { requiresAuth: true } },
  { path: "/compte/ateliers", name: "compte-ateliers", component: CompteAteliers, meta: { requiresAuth: true } },
  // Hub "Paramètres" natif : infos, ateliers, parrainage, notifications, informations légales, RGPD, déconnexion
  { path: "/compte/parametres", name: "compte-parametres", component: CompteParametres, meta: { requiresAuth: true } },
  { path: "/commande", name: "commande", component: () => import("../pages/commande/CommandeChoix.vue") },
  { path: "/commande/maison", name: "commande-maison", component: CommandeMaison },
  { path: "/commande/entreprise", name: "commande-entreprise", component: CommandeEntreprise },
  {
    path: "/commande/suivi/:orderId",
    name: "commande-suivi",
    component: CommandeSuivi,
    props: true,
  },
  { path: "/commande/succes", name: "commande-succes", component: () => import("../pages/commande/CommandeSucces.vue"), meta: { requiresAuth: true } },
  // Admin routes — only included on web or admin TestFlight build
  ...(showAdmin ? [
    { path: "/admin", name: "admin", component: AdminDashboard, meta: { requiresAdmin: true } },
    { path: "/admin/clients", name: "admin-clients", component: AdminClients, meta: { requiresAdmin: true } },
    { path: "/admin/creneaux", name: "admin-creneaux", component: AdminCreneaux, meta: { requiresAdmin: true } },
    { path: "/admin/commandes", name: "admin-commandes", component: AdminCommandes, meta: { requiresAdmin: true } },
    // Production V2 = module canonique : l'ancien module (AdminProduction.vue,
    // illisible sur mobile) n'est plus atteignable, on redirige les vieux
    // liens/bookmarks (dashboard, conseiller IA, favoris) vers la liste V2.
    { path: "/admin/production", redirect: "/admin/productions-v2" },
    { path: "/admin/production/:productionId", redirect: "/admin/productions-v2" },
    { path: "/admin/planning", name: "admin-planning", component: AdminPlanning, meta: { requiresAdmin: true } },
    { path: "/admin/stock", name: "admin-stock", component: AdminStock, meta: { requiresAdmin: true } },
    { path: "/admin/plats", name: "admin-plats", component: AdminPlats, meta: { requiresAdmin: true } },
    { path: '/admin/blocs', name: 'admin-blocs', component: AdminBlocs, meta: { requiresAdmin: true } },
    { path: '/admin/catalogue', name: 'admin-catalogue', component: AdminCatalogue, meta: { requiresAdmin: true } },
    { path: '/admin/etiquettes/:orderId', name: 'admin-etiquettes', component: AdminEtiquettes, meta: { requiresAdmin: true } },
    // Etiquette hors commande (test cuisine, don, vente directe). Chemin distinct
    // de /admin/etiquettes/:orderId, qui capterait "libre" comme un orderId.
    { path: '/admin/etiquettes-libres', name: 'admin-etiquettes-libres', component: () => import('../pages/admin/AdminLabels.vue'), meta: { requiresAdmin: true } },
    { path: "/admin/livraison", name: "admin-livraison", component: AdminLivraison, meta: { requiresAdmin: true } },
    { path: "/admin/tournee/:roundId", name: "admin-tournee", component: () => import("../pages/admin/AdminTourneeRun.vue"), meta: { requiresAdmin: true } },
    { path: "/admin/live-tracker", name: "admin-live-tracker", component: () => import("../pages/admin/AdminLiveTracker.vue"), meta: { requiresAdmin: true } },
    { path: "/admin/promos", name: "admin-promos", component: () => import("../pages/admin/AdminPromos.vue"), meta: { requiresAdmin: true } },
    { path: "/admin/communication", name: "admin-communication", component: AdminCommunication, meta: { requiresAdmin: true } },
    { path: "/admin/statistiques", name: "admin-analytics", component: AdminAnalytics, meta: { requiresAdmin: true } },
    { path: "/admin/ateliers", name: "admin-ateliers", component: AdminAteliers, meta: { requiresAdmin: true } },
    { path: "/admin/ateliers/nouveau", name: "admin-atelier-new", component: AdminAtelierEdit, meta: { requiresAdmin: true } },
    { path: "/admin/ateliers/:id/edit", name: "admin-atelier-edit", component: AdminAtelierEdit, props: true, meta: { requiresAdmin: true } },
    { path: "/admin/ateliers/:id/inscriptions", name: "admin-atelier-inscriptions", component: AdminAtelierInscriptions, props: true, meta: { requiresAdmin: true } },
    { path: '/admin/productions-v2', name: 'admin-productions-v2', component: AdminProductionsV2, meta: { requiresAdmin: true } },
    { path: '/admin/productions-v2/:id', name: 'admin-production-v2-hub', component: AdminProductionV2Hub, props: true, meta: { requiresAdmin: true } },
    { path: '/admin/rapports', name: 'admin-rapports', component: () => import('../pages/admin/AdminRapports.vue'), meta: { requiresAdmin: true } },
    { path: '/admin/avis', name: 'admin-avis', component: () => import('../pages/admin/AdminAvis.vue'), meta: { requiresAdmin: true } },
    { path: '/admin/contacts', name: 'admin-contacts', component: () => import('../pages/admin/AdminContacts.vue'), meta: { requiresAdmin: true } },
    { path: '/admin/inauguration', name: 'admin-inauguration', component: () => import('../pages/admin/AdminInauguration.vue'), meta: { requiresAdmin: true } },
  ] : []),
  { path: "/prototypes/a", name: "proto-a", component: ProtoA },
  { 
    path: "/t/:id", 
    name: "traceability", 
    component: () => import("@/pages/public/TraceabilityPlat.vue"),
    meta: { title: "Fiche Traçabilité" }
  },
  { path: "/:pathMatch(.*)*", name: "not-found", component: NotFound },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    // Ancre (ex. /mentions-legales#suppression, lien de suppression de compte declare au Play Store)
    if (to.hash) return { el: to.hash, top: 96 };
    return { top: 0 };
  },
});

// Native auth gate: redirect unauthenticated native users to /welcome
if (isNative) {
  const nativeGuestAllowedPaths = ['/welcome', '/decouvrir', '/auth/email', '/auth/connexion', '/auth/inscription', '/auth/mot-de-passe-oublie', '/commande/maison', '/concept', '/livraison', '/faq', '/contact', '/conservation', '/cgv', '/mentions-legales'];
  const nativeGuestAllowedPrefixes = ['/note/', '/compte/profils/'];
  router.beforeEach((to) => {
    const authStore = useAuthStore();
    // Use localStorage hint while Firebase auth resolves to avoid flash redirect
    const probablyLoggedIn = authStore.isLoggedIn || localStorage.getItem('isLoggedIn') === 'true';

    const isAllowed = nativeGuestAllowedPaths.includes(to.path)
      || nativeGuestAllowedPrefixes.some(p => to.path.startsWith(p));

    // Guest trying to access app → send to welcome
    if (!probablyLoggedIn && !isAllowed) {
      return { path: '/welcome', replace: true };
    }
    // Logged in user trying to go to welcome → send home
    if (authStore.isLoggedIn && to.path === '/welcome') {
      return { path: '/', replace: true };
    }
  });
}

// Web/native: enforce auth/admin meta on all routes
const AUTH_READY_TIMEOUT_MS = 5000;
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const requiresAuth = to.matched.some(r => r.meta?.requiresAuth);
  const requiresAdmin = to.matched.some(r => r.meta?.requiresAdmin);
  if (!requiresAuth && !requiresAdmin) return;

  // Wait for auth to be ready before deciding (avoid flicker on hard refresh).
  // Borne a 5 s : passe ce delai on continue plutot que de laisser la
  // navigation suspendue sans message.
  let authTimedOut = false;
  if (!authStore.isAuthReady) {
    await new Promise((resolve) => {
      const timer = setTimeout(() => {
        stop();
        authTimedOut = true;
        console.warn('[router] auth non prete apres 5 s, navigation sans attendre');
        resolve();
      }, AUTH_READY_TIMEOUT_MS);
      const stop = watch(() => authStore.isAuthReady, (ready) => {
        if (ready) { clearTimeout(timer); stop(); resolve(); }
      }, { immediate: true });
    });
  }
  // Auth toujours en attente (reseau lent, Firebase hors ligne) mais session
  // connue de la derniere fois : on laisse passer, la page se protegera
  // elle-meme, plutot que de renvoyer une personne connectee a la connexion.
  if (authTimedOut && !authStore.isLoggedIn && localStorage.getItem('isLoggedIn') === 'true') {
    if (requiresAdmin) return { path: '/', replace: true };
    return;
  }

  // Onboarding local-first natif : le quiz de gouts est accessible sans compte.
  if (to.meta.guestOnboarding && isNative && String(to.query.onboarding) === '1') {
    return;
  }

  if (!authStore.isLoggedIn) {
    return { path: '/auth/connexion', query: { redirect: to.fullPath }, replace: true };
  }
  if (requiresAdmin && !authStore.isAdmin) {
    return { path: '/', replace: true };
  }
  // Onboarding web deja fait : /bienvenue ne se rejoue pas (sauf ?redo=1).
  if (to.name === 'web-welcome' && authStore.user?.onboardingCompletedAt && String(to.query.redo) !== '1') {
    return { path: '/commande/maison', replace: true };
  }
});

// Journal de presence : au plus une ecriture toutes les 5 minutes, sans la page
// courante (RGPD : on garde seulement "vu pour la derniere fois" et le nom affiche).
const PRESENCE_INTERVAL_MS = 5 * 60 * 1000;
let lastPresenceAt = 0;
let lastPresenceUid = null;

router.afterEach((to) => {
  const authStore = useAuthStore();
  
  // 1. Presence / Live Users (Firestore)
  // Track EVERYONE who is logged in (including Admins) so they can verify they are online
  const user = authStore.isLoggedIn ? authStore.user : null;
  if (user?.uid && db) {
    const now = Date.now();
    if (user.uid !== lastPresenceUid || now - lastPresenceAt >= PRESENCE_INTERVAL_MS) {
      lastPresenceAt = now;
      lastPresenceUid = user.uid;
      setDoc(doc(db, 'online_users', user.uid), {
        uid: user.uid,
        displayName: user.displayName || 'Utilisateur',
        lastSeen: serverTimestamp(),
        role: user.role || 'client'
      }, { merge: true }).catch((e) => console.warn('Presence update failed', e));
    }
  }

  // 2. Statistics / Analytics (GA4)
  // Track ONLY non-admins to avoid polluting business stats
  if (!authStore.isAdmin) {
    trackPage(to.fullPath);
  }
});

export default router;
